import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { BsFileEarmarkArrowUpFill, BsFileEarmarkFill } from "react-icons/bs";

const TITLE_FONT = { fontFamily: "'Bebas Neue', 'Oswald', Impact, sans-serif" };
const EMPTY_FILE_MESSAGE = "File is empty please select the file with data in it";
const IGNORED_SHEETS = ["Lists", "_Districts", "Notes"];

const CELL = "border-b border-r border-gray-200 px-4 align-middle last:border-r-0";
const HEAD = "whitespace-nowrap py-4 font-semibold";

const STAGE = {
  IDLE: "idle",
  CHECKING: "checking",
  SELECTED: "selected",
  INVALID_FILE: "invalidFile",
  VALIDATING: "validating",
  READY: "ready",
  CONFIRM: "confirm",
  UPLOADING: "uploading",
};

async function checkExcelFile(file, preferredSheet) {
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });

  let sheetName;
  if (preferredSheet) {
    if (!workbook.SheetNames.includes(preferredSheet)) {
      return { ok: false, message: `"${preferredSheet}" sheet is not there` };
    }
    sheetName = preferredSheet;
  } else {
    sheetName =
      workbook.SheetNames.find((name) => !IGNORED_SHEETS.includes(name)) ||
      workbook.SheetNames[0];
  }

  const sheet = sheetName && workbook.Sheets[sheetName];
  if (!sheet) {
    return { ok: false, message: EMPTY_FILE_MESSAGE };
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: "",
  });
  const hasRows = rows
    .slice(1)
    .some((row) => row.some((cell) => String(cell).trim() !== ""));

  if (!hasRows) {
    return { ok: false, message: EMPTY_FILE_MESSAGE };
  }
  return { ok: true };
}

function DialogShell({
  title,
  onClose,
  closeDisabled,
  className = "",
  titleClassName = "text-[28px]",
  children,
}) {
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 p-4">
      <div
        className={`flex max-h-[92vh] w-full flex-col rounded-xl bg-white shadow-2xl ${className}`}
      >
        <div className="flex items-center justify-between gap-4 px-5 pt-4">
          <h2
            style={TITLE_FONT}
            className={`${titleClassName} leading-none tracking-[0.02em] text-primary`}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <div className="mx-3 mt-3 border-b border-gray-200" />
        {children}
      </div>
    </div>,
    document.body
  );
}

function ActionButton({ tone, disabled, onClick, children, small = false }) {
  const tones = {
    red: "bg-[#e50000] hover:bg-[#cc0000]",
    green: "bg-primary hover:brightness-95",
    // Soft teal used for NEXT in the reference modal
    muted: "bg-[#7eb8a8] hover:bg-[#6aa894]",
  };
  const size = small
    ? "h-10 min-w-[8.5rem] text-sm"
    : "h-10 min-w-[9rem] text-[15px]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${size} rounded-lg px-8 font-semibold uppercase tracking-wide text-white shadow-sm transition ${tones[tone]} disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-[#7eb8a8]`}
    >
      {children}
    </button>
  );
}

/**
 * Two-step Excel bulk upload: NEXT validates the file, UPLOAD saves it.
 *
 * onValidate / onUpload receive the File and must resolve to
 * { ok: boolean, message?: string, total?: number, invalidRows?: [{ row, values, fields, errors }] }.
 * After a successful validate, Upload opens a confirm step; Proceed runs onUpload.
 */
export default function BulkUploadModal({
  isOpen,
  onClose,
  title = "UPLOAD DATA",
  columns = [],
  onValidate,
  onUpload,
  preferredSheet,
  maxRecords = 100,
  confirmEntitySingular = "record",
  confirmEntityPlural = "records",
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState(STAGE.IDLE);
  const [message, setMessage] = useState("");
  const [invalidRows, setInvalidRows] = useState([]);
  const [readyCount, setReadyCount] = useState(0);

  const reset = () => {
    setFile(null);
    setStage(STAGE.IDLE);
    setMessage("");
    setInvalidRows([]);
    setReadyCount(0);
  };

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setStage(STAGE.IDLE);
    setMessage("");
    setInvalidRows([]);
    setReadyCount(0);
  }, [isOpen]);

  if (!isOpen) return null;

  const busy = [STAGE.CHECKING, STAGE.VALIDATING, STAGE.UPLOADING].includes(stage);
  const showingConfirm = stage === STAGE.CONFIRM || stage === STAGE.UPLOADING;

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleFileChange = async (nextFile) => {
    if (!nextFile) return;
    setFile(nextFile);
    setInvalidRows([]);
    setMessage("");
    setReadyCount(0);

    if (!/\.xlsx$/i.test(nextFile.name || "")) {
      setStage(STAGE.INVALID_FILE);
      setMessage("Please select an Excel (.xlsx) file");
      return;
    }

    setStage(STAGE.CHECKING);
    try {
      const check = await checkExcelFile(nextFile, preferredSheet);
      if (check.ok) {
        setStage(STAGE.SELECTED);
      } else {
        setStage(STAGE.INVALID_FILE);
        setMessage(check.message || EMPTY_FILE_MESSAGE);
      }
    } catch {
      // Unreadable here; the server reports the exact problem on NEXT.
      setStage(STAGE.SELECTED);
    }
  };

  const applyResult = (result, fallbackStage) => {
    if (result?.invalidRows?.length) {
      setInvalidRows(result.invalidRows);
      setStage(STAGE.SELECTED);
      return;
    }
    setStage(fallbackStage);
    setMessage(result?.message || "Something went wrong. Please try again.");
  };

  const handleNext = async () => {
    if (!file || stage !== STAGE.SELECTED) return;
    setStage(STAGE.VALIDATING);
    setMessage("");
    const result = await onValidate(file);
    if (result?.ok) {
      setReadyCount(Number(result.total) || 0);
      setStage(STAGE.READY);
      setMessage(`${file.name} is ready to Upload.`);
    } else {
      applyResult(result, STAGE.INVALID_FILE);
    }
  };

  const handleUpload = () => {
    if (!file || stage !== STAGE.READY) return;
    setStage(STAGE.CONFIRM);
  };

  const handleConfirmClose = () => {
    if (stage === STAGE.UPLOADING) return;
    setStage(STAGE.READY);
  };

  const handleProceed = async () => {
    if (!file || stage !== STAGE.CONFIRM) return;
    setStage(STAGE.UPLOADING);
    setMessage("");
    const result = await onUpload(file);
    if (result?.ok) {
      reset();
      onClose();
    } else {
      applyResult(result, STAGE.READY);
    }
  };

  const handleReupload = () => {
    reset();
  };

  const showingFailure = invalidRows.length > 0;
  const isReady =
    stage === STAGE.READY || stage === STAGE.CONFIRM || stage === STAGE.UPLOADING;
  const isValidating = stage === STAGE.CHECKING || stage === STAGE.VALIDATING;
  const messageIsError = !!message && stage !== STAGE.READY && !showingConfirm;
  const confirmLabel =
    readyCount === 1 ? confirmEntitySingular : confirmEntityPlural;
  const confirmMessage = `${readyCount} ${confirmLabel} will be created.`;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(event) => {
          handleFileChange(event.target.files?.[0] || null);
          event.target.value = "";
        }}
      />

      {showingFailure ? (
        <DialogShell
          title="DATA VALIDATION FAILURE"
          onClose={handleClose}
          className="max-w-[1180px]"
          titleClassName="text-[28px]"
        >
          <div className="mx-4 mt-3 h-[55vh] min-h-[220px] overflow-auto border border-gray-200">
            <table className="min-w-full border-collapse text-left text-[13px] text-gray-800">
              <thead className="sticky top-0 z-10 bg-[#f3f4f6]">
                <tr>
                  <th className={`${CELL} ${HEAD}`}>#</th>
                  {columns.map((col) => (
                    <th key={col.key} className={`${CELL} ${HEAD}`}>
                      {col.label}
                    </th>
                  ))}
                  <th className={`${CELL} ${HEAD}`}>Validation Error</th>
                </tr>
              </thead>
              <tbody>
                {invalidRows.map((item, index) => {
                  const badFields = new Set(item.fields || []);
                  return (
                    <tr key={item.row} className="bg-white">
                      <td className={`${CELL} py-5`}>{index + 1}</td>
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={`${CELL} py-5 ${badFields.has(col.key) ? "text-red-600" : ""}`}
                        >
                          {item.values?.[col.key] || "-"}
                        </td>
                      ))}
                      <td className={`${CELL} min-w-[280px] py-5 text-red-600`}>
                        {(item.errors || []).join(", ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-5 px-6 text-center text-sm text-red-600">
            There are 1 or more incorrect data in the Excel. Please correct the ones shown in red
            and reupload the file.
          </p>
          <div className="mb-5 mt-4 flex items-center justify-center gap-4">
            <ActionButton tone="red" small onClick={handleClose}>
              Close
            </ActionButton>
            <ActionButton tone="green" small onClick={handleReupload}>
              Reupload
            </ActionButton>
          </div>
        </DialogShell>
      ) : showingConfirm ? null : (
        <DialogShell
          title={title}
          onClose={handleClose}
          closeDisabled={busy}
          className="max-w-[560px]"
        >
          <div className="flex flex-col items-center px-6 pb-6">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="mt-5 flex h-[185px] w-[205px] flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-primary bg-white px-4 text-center disabled:cursor-wait"
            >
              {isValidating ? (
                <>
                  <span className="text-base text-primary">Validating...</span>
                  <span className="break-all text-sm text-gray-500">{file?.name}</span>
                </>
              ) : file ? (
                <>
                  <BsFileEarmarkFill size={32} className="text-primary" />
                  <span className="text-[15px] font-semibold text-primary">File Selected</span>
                  <span className="line-clamp-2 break-all text-sm text-gray-500">{file.name}</span>
                </>
              ) : (
                <>
                  <BsFileEarmarkArrowUpFill size={32} className="text-primary" />
                  <span className="text-[15px] text-gray-500">
                    Click Here To Upload
                  </span>
                </>
              )}
            </button>

            {message && (
              <p
                className={`mt-3 max-w-[480px] text-center text-[15px] ${
                  messageIsError ? "text-red-600" : "text-primary"
                }`}
              >
                {message}
              </p>
            )}

            <div className="mt-3 flex items-center justify-center gap-3">
              <ActionButton tone="red" onClick={handleClose} disabled={busy}>
                Close
              </ActionButton>
              {isReady ? (
                <ActionButton
                  tone="green"
                  onClick={handleUpload}
                  disabled={showingConfirm}
                >
                  Upload
                </ActionButton>
              ) : (
                <ActionButton
                  tone={stage === STAGE.SELECTED ? "green" : "muted"}
                  onClick={handleNext}
                  disabled={stage !== STAGE.SELECTED}
                >
                  Next
                </ActionButton>
              )}
            </div>

            <p className="mt-4 text-center text-[14px] text-[#c05a11]">
              Note : Maximum recommended number of records is {maxRecords} per Excel.
            </p>
          </div>
        </DialogShell>
      )}

      {showingConfirm && (
        <DialogShell
          title={title}
          onClose={handleConfirmClose}
          closeDisabled={stage === STAGE.UPLOADING}
          className="max-w-[520px]"
        >
          <div className="flex flex-col items-center px-8 py-10">
            <p className="text-center text-[18px] font-medium text-primary">
              {confirmMessage}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <ActionButton
                tone="red"
                onClick={handleConfirmClose}
                disabled={stage === STAGE.UPLOADING}
              >
                Close
              </ActionButton>
              <ActionButton
                tone="green"
                onClick={handleProceed}
                disabled={stage === STAGE.UPLOADING}
              >
                {stage === STAGE.UPLOADING ? "Uploading..." : "Proceed"}
              </ActionButton>
            </div>

            <p className="mt-4 text-center text-[14px] text-[#c05a11]">
              Note : Maximum recommended number of records is {maxRecords} per Excel.
            </p>
          </div>
        </DialogShell>
      )}
    </>
  );
}
